<template>
  <b-card no-body>
    <b-card-header
      class="p-1 d-flex align-items-center justify-content-between flex-wrap"
    >
      <div class="d-flex align-items-center flex-wrap">
        <h4 class="mb-0 mr-1">{{ $t("Scholarship Summary") }}</h4>
        <b-button
          v-if="!isHeadOffice"
          variant="outline-primary"
          size="sm"
          :disabled="dataLoading"
          @click="$bvModal.show(filterModalId)"
        >
          <feather-icon icon="FilterIcon" class="mr-50" />
          {{ $t("Filter") }}
        </b-button>
      </div>
      <div class="d-flex align-items-center flex-wrap mt-25 mt-sm-0">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="dataLoading"
          @click="printScholarshipSummary"
        >
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          {{ $t("Print") }}
        </b-button>
        <b-button
          variant="outline-success"
          size="sm"
          class="ml-50"
          :disabled="dataLoading"
          @click="exportScholarshipExcel"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
          {{ $t("Excel") }}
        </b-button>
      </div>
    </b-card-header>
    <b-card-body class="px-1 pb-1">
      <b-row class="scholarship-stats-card">
        <b-col md="4" cols="12">
          <div class="scholarship-card-new border-top-primary">
            <div
              class="d-flex justify-content-between align-items-center mb-25"
            >
              <span class="scholarship-card-title">Total Assigned</span>
              <div class="scholarship-card-icon-container bg-light-primary">
                <feather-icon icon="UsersIcon" size="20" />
              </div>
            </div>
            <h2 class="scholarship-card-value font-weight-bolder text-primary">
              {{ scholarshipTotals.assigned }}
            </h2>
            <div
              v-if="assignedStrength.length"
              class="d-flex justify-content-between text-dark font-small-3"
            >
              <div
                v-for="(cls, cInd) in assignedStrength"
                :key="'assigned-' + cInd"
              >
                <span>{{ getClassName(cls) }}:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  getClassStrengthValue(cls)
                }}</span>
              </div>
            </div>
          </div>
        </b-col>

        <b-col md="4" cols="12">
          <div class="scholarship-card-new border-top-success">
            <div
              class="d-flex justify-content-between align-items-center mb-25"
            >
              <span class="scholarship-card-title">Total Accepted</span>
              <div class="scholarship-card-icon-container bg-light-success">
                <feather-icon icon="CheckCircleIcon" size="20" />
              </div>
            </div>
            <h2 class="scholarship-card-value font-weight-bolder text-success">
              {{ scholarshipTotals.accepted }}
            </h2>
            <div
              v-if="acceptedStrength.length"
              class="d-flex justify-content-between text-dark font-small-3"
            >
              <div
                v-for="(cls, cInd) in acceptedStrength"
                :key="'accepted-' + cInd"
              >
                <span>{{ getClassName(cls) }}:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  getClassAcceptedValue(cls)
                }}</span>
              </div>
            </div>
            <hr class="scholarship-card-divider" />
            <div class="d-flex justify-content-between text-dark font-small-3">
              <div>
                <span>App:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  scholarshipTotals.acceptedAppLogin
                }}</span>
              </div>
              <div class="text-right">
                <span>Admin:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  scholarshipTotals.acceptedAdminPortal
                }}</span>
              </div>
            </div>
          </div>
        </b-col>

        <b-col md="4" cols="12">
          <div class="scholarship-card-new border-top-warning">
            <div
              class="d-flex justify-content-between align-items-center mb-25"
            >
              <span class="scholarship-card-title">Total Not Accepted</span>
              <div class="scholarship-card-icon-container bg-light-warning">
                <feather-icon icon="XCircleIcon" size="20" />
              </div>
            </div>
            <h2 class="scholarship-card-value font-weight-bolder text-warning">
              {{ scholarshipTotals.notAccepted }}
            </h2>
            <div
              v-if="notAcceptedStrength.length"
              class="d-flex justify-content-between text-dark font-small-3"
            >
              <div
                v-for="(cls, cInd) in notAcceptedStrength"
                :key="'not-accepted-' + cInd"
              >
                <span>{{ getClassName(cls) }}:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  getClassNotAcceptedValue(cls)
                }}</span>
              </div>
            </div>
            <hr class="scholarship-card-divider" />
            <div class="d-flex justify-content-between text-dark font-small-3">
              <div>
                <span>N/A:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  remainingNotAccepted
                }}</span>
              </div>
              <div class="text-right">
                <span>Left:</span>
                <span class="font-weight-bolder text-dark ml-25">{{
                  scholarshipTotals.notAcceptedLeft
                }}</span>
              </div>
            </div>
          </div>
        </b-col>

        <!-- <b-col md="4" cols="12">
          <div class="stats-card bg-gradient-warning p-50">
            <div class="d-flex align-items-center">
              <div class="avatar bg-white rounded p-50 mr-1">
                <feather-icon icon="XIcon" size="24" class="text-warning" />
              </div>
              <div class="flex-grow-1">
                <small class="text-white d-block mb-25"
                  >Total Not Accepted</small
                >
                <h2 class="text-white mb-0 font-weight-bolder">
                  {{ scholarshipTotals.notAccepted }}
                </h2>
              </div>
            </div>
          </div>
        </b-col> -->
      </b-row>

      <b-row class="mt-1 border-top" v-if="isHeadOffice">
        <b-col lg="4" md="6">
          <b-form-group
            :label="$t('Accepted By')"
            label-class="font-weight-bold"
            label-cols-md="6"
            class="mb-0 mt-50"
          >
            <v-select
              v-model="acceptedBy"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="filterOptions"
              :clearable="false"
              label="text"
              :reduce="(val) => val.value"
              :disabled="dataLoading"
              :placeholder="$t('Select')"
              @input="LoadData()"
            />
          </b-form-group>
        </b-col>
        <b-col lg="8" md="6">
          <div v-if="category && category.length > 0" class="mt-50 ml-50">
            <b-badge variant="light-primary" class="text-wrap">
              {{ category.join(", ") }}
            </b-badge>
          </div>
          <div v-if="region && region.length > 0" class="mt-50 ml-50">
            <b-badge variant="light-primary" class="text-wrap">
              {{ region.join(", ") }}
            </b-badge>
          </div>
        </b-col>
      </b-row>

      <!-- Scholarship Filter Modal -->
      <b-modal
        :id="filterModalId"
        :title="$t('Scholarship Filters')"
        centered
        hide-footer
        no-close-on-backdrop
      >
        <b-form-group :label="$t('Accepted By')">
          <v-select
            v-model="acceptedBy"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filterOptions"
            :clearable="false"
            label="text"
            :reduce="(val) => val.value"
            :disabled="dataLoading"
            :placeholder="$t('Select')"
          />
        </b-form-group>
        <b-form-group
          v-if="!isPcp && !isUtp"
          :label="$t('Scholarship Category')"
        >
          <v-select
            multiple
            :closeOnSelect="false"
            v-model="category"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="scholarshipCategoryOptions"
            :clearable="false"
            class="form-select-custom"
            :disabled="dataLoading"
            :placeholder="$t('Select scholarship category')"
          />
        </b-form-group>
        <b-form-group :label="$t('Region')">
          <v-select
            multiple
            :closeOnSelect="false"
            v-model="region"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="regionOptions"
            :clearable="false"
            class="form-select-custom"
            :disabled="dataLoading"
            :placeholder="$t('Select region')"
          />
        </b-form-group>
        <b-form-group :label="$t('Fiscal Year')" v-if="isUtp">
        <v-select
          multiple
          :closeOnSelect="false"
          v-model="selectedFiscalYear"
          :options="utpFiscalOptions"
          :clearable="false"
          class="form-select-custom"
          :disabled="dataLoading"
          :placeholder="$t('Select fiscal year')"
        />
      </b-form-group>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="$bvModal.hide(filterModalId)"
          >
            {{ $t("Cancel") }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="dataLoading"
            @click="
              LoadData();
              $bvModal.hide(filterModalId);
            "
          >
            {{ $t("Apply") }}
          </b-button>
        </div>
      </b-modal>

      <!-- <b-row>
        <b-col
          md="12"
          class="d-flex flex-wrap order-1 order-lg-0"
          style="gap: 1rem"
        >
          <div>
            <div class="mb-25">
              <span class="font-weight-bold">{{ $t("Total Assigned") }}: </span>
              <b-badge variant="light-primary">
                {{ scholarshipTotals.assigned }}
              </b-badge>
            </div>
          </div>
          <div>
            <div class="mb-25">
              <span class="font-weight-bold">{{ $t("Accepted") }}: </span>
              <b-badge variant="light-primary">
                {{ scholarshipTotals.accepted }}
              </b-badge>
            </div>
          </div>
          <div>
            <div class="mb-25">
              <span class="font-weight-bold">{{ $t("App") }}: </span>
              <b-badge variant="light-primary">
                {{ scholarshipTotals.acceptedAppLogin }}
              </b-badge>
            </div>
          </div>
          <div>
            <div class="mb-25">
              <span class="font-weight-bold">{{ $t("Admin") }}: </span>
              <b-badge variant="light-primary">
                {{ scholarshipTotals.acceptedAdminPortal }}
              </b-badge>
            </div>
          </div>
          <div>
            <div class="mb-25">
              <span class="font-weight-bold">{{ $t("Not Accepted") }}: </span>
              <b-badge variant="light-primary">
                {{ scholarshipTotals.notAccepted }}
              </b-badge>
            </div>
          </div>
        </b-col>
      </b-row> -->
    </b-card-body>
    <b-table
      class=""
      sticky-header="350px"
      show-empty
      :items="stdItem"
      :fields="computedFields"
      responsive
      hover
      :busy="dataLoading"
      small
    >
      <template #table-busy>
        <div class="d-flex justify-content-center mb-3 mt-3">
          <b-spinner
            style="width: 3rem; height: 3rem"
            type="grow"
            variant="primary"
            label="Spinning"
          ></b-spinner>
        </div>
      </template>

      <template #head()="scope">
        <div>
          {{ $t(scope.label) }}
        </div>
      </template>

      <template #cell(student)="{ item }">
        <b-media vertical-align="center">
          <template #aside>
            <b-avatar
              size="44"
              :src="item.student.picture"
              variant="light-primary"
            />
          </template>

          <b-link class="font-weight-bold d-block text-nowrap mb-25">
            <b-badge variant="light-primary">
              {{ item.student.name }}
            </b-badge>
          </b-link>
          <b-link class="font-weight-bold d-block text-nowrap mb-25">
            <b-badge variant="light-primary">
              {{ $t("F/N") }}: {{ item.student.father_name }}
            </b-badge>
          </b-link>
          <small class="text-muted">
            <b-badge variant="light-primary">
              FB Reg. No. {{ item.studentDetails.federalRegNo || "-" }}
            </b-badge>
          </small>
          <div v-if="isUtp && item.studentDetails.utpEnrolledOn" class="mt-25">
            <b-badge variant="light-primary">
              {{
                new Date(item.studentDetails.utpEnrolledOn).toLocaleDateString(
                                   "en-UK",
                                   {
                                     year: "numeric",
                                     day: "numeric",
                                     month: "short",
                                   }
                                 )
                               }}
            </b-badge>
          </div> 
        </b-media>
      </template>

      <template #cell(class)="{ item }">
        <b-badge variant="light-primary">
          {{ item.student.meta_class || "-" }}
        </b-badge>
      </template>
      <template #cell(course)="{ item }">
        <b-badge variant="light-primary">
          {{ item.studentDetails.utpCourse || "-" }}
        </b-badge>
      </template>
      <template #cell(scholarship)="{ item }">
        <b-badge
          :variant="scholarshipVariant(item.studentDetails.scholarshipStatus)"
        >
          {{ item.studentDetails.scholarshipStatus || "—" }}
        </b-badge>
      </template>
      <template #cell(status)="{ item }">
        <b-badge :variant="statusVariant(item.studentDetails.studentStatus)">
          {{ item.studentDetails.studentStatus || "—" }}
        </b-badge>
      </template>
      <template #cell(action)="{ item }">
        <b-button
          v-if="
            ['registered', 'enrolled'].includes(
              item.student.status.toLowerCase()
            ) &&
            item.studentDetails.utpSupport === 'yes' &&
            isAccepted(item.studentDetails)
          "
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          class="btn-icon mr-1"
          v-b-tooltip.hover.top
          :title="$t(isHeadOffice ? 'Conditionally Verify' : 'Verify')"
          @click="openVerify(item.student)"
        >
          <feather-icon icon="CheckIcon" />
        </b-button>
        <b-button
          v-else-if="
            ['present'].includes(item.student.status.toLowerCase()) &&
            item.studentDetails.utpSupport === 'yes' &&
            !item.studentDetails.utpConfirmation
          "
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          class="btn-icon mr-1"
          v-b-tooltip.hover.top
          :title="'Confirmation'"
          @click="openConfirm(item.student)"
        >
          <feather-icon icon="CheckCircleIcon" />
        </b-button>
      </template>
    </b-table>

    <!-- Verification Confirmation Modal -->
    <b-modal
      id="modal-scholarship-confirm"
      v-model="verifyModalVisible"
      :title="$t('Verify Student')"
      centered
      no-close-on-backdrop
      :ok-title="$t('Verify')"
      :cancel-title="$t('Cancel')"
      ok-variant="success"
      cancel-variant="outline-secondary"
      :ok-disabled="verifying"
      @ok.prevent="acceptVerification"
    >
      <div v-if="verifyStd" class="py-50">
        <p class="mb-1">
          {{ $t("Are you sure you want to verify this student:") }}
        </p>
        <div class="d-flex flex-column" style="gap: 0.4rem">
          <div>
            <span class="font-weight-bolder">{{ $t("Student") }}: </span>
            <span>{{ verifyStd.name }}</span>
          </div>
          <div>
            <span class="font-weight-bolder">{{ $t("Father") }}: </span>
            <span>{{ verifyStd.father_name }}</span>
          </div>
          <div>
            <span class="font-weight-bolder">{{ $t("Class") }}: </span>
            <span>{{ verifyStd.meta_class }}</span>
          </div>
        </div>
        <div class="pt-50 mt-50 border-top" v-if="isHeadOffice">
          <b-form-group
            :label="
              $t(
                'You\'re conditionally verifying this student. Please enter the comment below.'
              )
            "
            :invalid-feedback="$t('Comment is required.')"
            label-class="font-weight-bolder"
          >
            <b-form-textarea
              :placeholder="$t('Enter comment')"
              rows="3"
              :disabled="!verifyObj.isConditional"
              v-model.trim="verifyObj.comment"
            />
          </b-form-group>
        </div>
      </div>
      <template #modal-ok>
        <b-spinner v-if="verifying" small type="grow" class="mr-50" />
        <span>{{ $t("Verify") }}</span>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BTable,
  BBadge,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BFormGroup,
  BMedia,
  BAvatar,
  BModal,
  BFormTextarea,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import * as XLSX from "xlsx";

import { mapActions, mapGetters } from "vuex";

export default {
  name: "AttendanceCard",
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BFormGroup,
    BMedia,
    BAvatar,
    BModal,
    BFormTextarea,
    flatPickr,
    vSelect,
  },
  props: {
    campusID: {
      type: Number,
    },
    isHeadOffice: {
      type: Boolean,
      default: false,
    },
    region: {
      type: Array,
      default: () => [],
    },
    category: {
      type: Array,
      default: () => [],
    },
    scholarshipCategoryOptions: {
      type: Array,
      default: () => [],
    },
    regionOptions: {
      type: Array,
      default: () => [],
    },
    tabValue: {
      type: String,
      default: "none",
    },
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      config2: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      scholarshipTotals: {
        assigned: 0,
        accepted: 0,
        acceptedAppLogin: 0,
        acceptedAdminPortal: 0,
        acceptedPerc: 0,
        notAccepted: 0,
        notAcceptedLeft: 0,
        active: 0,
        onProbation: 0,
        inactive: 0,
      },
      assignedStrength: [],
      acceptedStrength: [],
      notAcceptedStrength: [],
      dataLoading: false,
      stdItem: [],
      stdFields: [
        { label: "Student", key: "student" },
        { label: "Class", key: "class" },
        { label: "scholarship", key: "scholarship" },
        { label: "status", key: "status" },
      ],
      utpFields: [
        { label: "Student", key: "student" },
        { label: "Course", key: "course" },
        { label: "scholarship", key: "scholarship" },
        { label: "status", key: "status" },
        { label: "action", key: "action" },
      ],
      acceptedBy: "",
      filterOptions: [
        { text: "All", value: "" },
        { text: "App", value: "app-login" },
        { text: "Admin", value: "admin-portal" },
      ],
      verifyModalVisible: false,
      verifyStd: null,
      verifyObj: {
        sID: 0,
        userID: 0,
        isConditional: false,
        comment: "",
      },
      verifying: false,
      utpFiscalOptions: ["2026-27", "2025-26", "2024-25", "2023-24"],
      selectedFiscalYear: []
    };
  },
  created() {
    this.LoadData();
  },
  computed: {
    isPcp() {
      return String(this.tabValue || "").toLowerCase() === "pcp";
    },
    isUtp() {
      return String(this.tabValue || "").toLowerCase() === "utp";
    },
    filterModalId() {
      return `modal-scholarship-card-filter-${this.tabValue || "none"}`;
    },
    remainingNotAccepted() {
      return (
        this.scholarshipTotals.notAccepted -
        this.scholarshipTotals.notAcceptedLeft
      );
    },
    computedFields() {
      return this.isUtp ? this.utpFields : this.stdFields;
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    statusVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "active") return "success";
      if (s === "in-active" || s === "inactive") return "danger";
      if (s === "on-probation") return "warning";
      return "secondary";
    },

    scholarshipVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "accepted") return "success";
      if (s === "not-accepted" || s === "not accepted") return "danger";
      if (s === "pending") return "warning";
      return "secondary";
    },

    parseScholarshipResponse(res) {
      if (!res) return {};
      if (
        res.data &&
        (res.status === "success" || res.data.items || res.data.totalCounts)
      ) {
        return res.data;
      }
      return res;
    },

    getClassName(cls) {
      return cls?.className ?? cls?.ClassName ?? "";
    },

    getClassStrengthValue(cls) {
      return cls?.strength ?? cls?.Strength ?? 0;
    },

    getClassAcceptedValue(cls) {
      return cls?.acceptedCount ?? cls?.AcceptedCount ?? 0;
    },

    getClassNotAcceptedValue(cls) {
      return cls?.stdCount ?? 0;
    },

    exportScholarshipExcel() {
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = `scholarship-summary-${stamp}.xlsx`;

      const kFederal = this.$t("Federal Board Reg No");
      const kStudent = this.$t("Student Name");
      const kFather = this.$t("Father Name");
      const kClass = this.$t("Class");
      const kSchCat = this.$t("Scholarship Category");
      const kSch = this.$t("Scholarship Status");
      const kStat = this.$t("Student Status");
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";
      const kStatus = "Current Status";

      const expData = (this.stdItem || []).map((item) => {
        const st = item.student || {};
        const det = item.studentDetails || {};
        if (this.isUtp) {
          return {
            [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
            [kStudent]: st.name || "",
            [kFather]: st.father_name || "",
            [kClass]: st.meta_class || "",
            [kWa]:
              st.contact_father != null && st.contact_father !== ""
                ? String(st.contact_father)
                : "",
            [kParentContact]:
              st.contact_mother != null && st.contact_mother !== ""
                ? String(st.contact_mother)
                : "",
            [kStudentContact]:
              st.contact_student != null && st.contact_student !== ""
                ? String(st.contact_student)
                : "",
            "Accepted By": det.acceptedBy || "",
            "Verified By": det.verifiedBy || "",
            "UTP Enrolled On": det.utpEnrolledOn
              ? new Date(det.utpEnrolledOn).toLocaleDateString("en-UK", {
                  year: "numeric",
                  day: "numeric",
                  month: "short",
                })
              : "",
            "UTP Confirmation": det.utpConfirmation || "",
            "UTP Course": det.utpCourse || "",
            "Mode of Class": det.utpClassMode || "",
            "UTP Comment": det.utpComment || "",
          };
        }

        return {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kClass]: st.meta_class || "",
          [kSchCat]: det.scholarshipCategory || "",
          [kSch]: det.scholarshipStatus || "",
          [kStat]: det.studentStatus || "",
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
          [kStatus]: st.status || "",
          "Accepted By": det.acceptedBy || "",
          "Verified By": det.verifiedBy || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Scholarship");
      XLSX.writeFile(workbook, fname);
    },

    escapeHtml(text) {
      if (text == null) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    async resolveCampusForPrint() {
      const campusId = this.campusID || this.$store.state.userData.cId;
      const branches = this.$store.state.branches || [];
      let match = branches.find((b) => String(b.id) === String(campusId));
      const hasDetail = (m) =>
        m && (m.branch || m.campusName || m.organizationName || m.name);
      if (hasDetail(match)) {
        return { campusId, ...match };
      }
      const cb = this.$store.state.currentBranch || {};
      if (String(cb.id) === String(campusId) && hasDetail(cb)) {
        return { campusId, ...cb };
      }
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const list = await this.get(obj);
        const arr = Array.isArray(list) ? list : [];
        match = arr.find((b) => String(b.id) === String(campusId));
        if (match) return { campusId, ...match };
      } catch (_e) {
        /* ignore */
      }
      return { campusId };
    },

    async printScholarshipSummary() {
      const campusMeta = await this.resolveCampusForPrint();
      const displayName =
        campusMeta.campusName ||
        campusMeta.organizationName ||
        campusMeta.name ||
        "";
      const branch = campusMeta.branch || "";

      const orgName = (
        campusMeta.organizationName ||
        campusMeta.name ||
        ""
      ).trim();
      const branchOrCampus = (branch || campusMeta.campusName || "").trim();
      const schoolPart = (
        orgName ||
        displayName ||
        branchOrCampus ||
        this.$t("School")
      ).trim();
      let campusSegment = "";
      if (
        branchOrCampus &&
        branchOrCampus.toLowerCase() !== schoolPart.toLowerCase()
      ) {
        campusSegment = branchOrCampus;
      }

      const schoolUpper = schoolPart.toUpperCase();
      const campusUpper = campusSegment.toUpperCase();

      const now = new Date();
      const periodStr = now.toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      });
      const generatedStr = now.toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const reportTitle = this.$t("Scholarship Status Report");
      const recordCount = (this.stdItem || []).length;

      const mainTitleHtml = campusUpper
        ? `<span class="school-name">${this.escapeHtml(
            schoolUpper
          )}</span><span class="title-dash"> — </span><span class="campus-name">${this.escapeHtml(
            campusUpper
          )}</span>`
        : `<span class="school-name">${this.escapeHtml(schoolUpper)}</span>`;

      const metaHtml = `${this.escapeHtml(
        reportTitle
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Period"
      )}:</strong> ${this.escapeHtml(
        periodStr
      )} <span class="meta-sep">|</span> <strong>${this.$t(
        "Records"
      )}:</strong> ${recordCount} <span class="meta-sep">|</span> <strong>${this.$t(
        "Generated on"
      )}</strong> ${this.escapeHtml(generatedStr)}`;

      const thead = `<tr>
        <th>${this.$t("Federal Board Reg No")}</th>
        <th>${this.$t("Student Name")}</th>
        <th>${this.$t("Father Name")}</th>
        <th>${this.$t("Class")}</th>
        ${
          this.isUtp
            ? `<th>${this.$t("UTP Enrolled On")}</th>
        <th>${this.$t("UTP Course")}</th>
        <th>${this.$t("Mode of Class")}</th>`
            : `<th>${this.$t("Scholarship Category")}</th>`
        }
        <th>${this.$t("Scholarship Status")}</th>
        <th>${this.$t("Student Status")}</th>
        <th>WhatsApp No</th>
        <th>Parent's Contact</th>
        <th>Student's Contact</th>
      </tr>`;

      const rows = (this.stdItem || [])
        .map((item) => {
          const st = item.student || {};
          const det = item.studentDetails || {};
          const schV = this.scholarshipVariant(det.scholarshipStatus || "");
          const stV = this.statusVariant(det.studentStatus || "");
          const fed = det.federalRegNo ? String(det.federalRegNo) : "—";
          const enrolledOn = det.utpEnrolledOn
            ? new Date(det.utpEnrolledOn).toLocaleDateString("en-UK", {
                year: "numeric",
                day: "numeric",
                month: "short",
              })
            : "—";
          const wa =
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "—";
          const parentC =
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "—";
          const studentC =
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "—";
          return `<tr>
            <td>${this.escapeHtml(fed)}</td>
            <td>${this.escapeHtml(st.name || "—")}</td>
            <td>${this.escapeHtml(st.father_name || "—")}</td>
            <td>${this.escapeHtml(st.meta_class || "—")}</td>
            ${
              this.isUtp
                ? `<td>${this.escapeHtml(enrolledOn)}</td>
            <td>${this.escapeHtml(det.utpCourse || "—")}</td>
            <td>${this.escapeHtml(det.utpClassMode || "—")}</td>`
                : `<td>${this.escapeHtml(det.scholarshipCategory || "—")}</td>`
            }
            <td class="pv-hl-${schV}">${this.escapeHtml(
            det.scholarshipStatus || "—"
          )}</td>
            <td class="pv-hl-${stV}">${this.escapeHtml(
            det.studentStatus || "—"
          )}</td>
            <td>${this.escapeHtml(wa)}</td>
            <td>${this.escapeHtml(parentC)}</td>
            <td>${this.escapeHtml(studentC)}</td>
          </tr>`;
        })
        .join("");

      const emptyRow = `<tr><td colspan="${
        this.isUtp ? 12 : 10
      }" class="text-center">${this.$t("No records")}</td></tr>`;

      const docTitle = this.$t("Scholarship Summary");

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${this.escapeHtml(
        docTitle
      )}</title>
<style>
  @page { size: A4 landscape; margin: 4mm; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #111; }
  .print-header { margin-bottom: 0; }
  .print-main-title {
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.25;
    margin: 0 0 10px;
  }
  .print-main-title .school-name { color: #000; }
  .print-main-title .title-dash { color: #000; font-weight: 700; }
  .print-main-title .campus-name { color: #4a4a4a; font-weight: 700; }
  .print-meta {
    font-size: 11px;
    color: #5c5c5c;
    font-weight: 500;
    line-height: 1.5;
    margin: 0 0 10px;
  }
  .print-meta .meta-sep {
    color: #9a9a9a;
    font-weight: 300;
    padding: 0 10px;
  }
  .print-meta strong { color: #444; font-weight: 600; }
  .print-header-rule {
    border: none;
    border-bottom: 3px solid #000;
    margin: 0 0 14px;
    padding: 0;
    height: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 5px 6px; text-align: left; vertical-align: top; }
  th { background: #e8e8e8; font-weight: 600; }
  tr:nth-child(even) td:not([class*="pv-hl-"]) { background: #fafafa; }
  td[class*="pv-hl-"] {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-weight: 600;
  }
  td.pv-hl-success { background: #cfe9de !important; color: #0f5132 !important; }
  td.pv-hl-danger { background: #f8d7da !important; color: #842029 !important; }
  td.pv-hl-warning { background: #fff3cd !important; color: #664d03 !important; }
  td.pv-hl-secondary { background: #e9ecef !important; color: #495057 !important; }
  .text-center { text-align: center; }
</style></head><body>
  <header class="print-header">
    <div class="print-main-title">${mainTitleHtml}</div>
    <div class="print-meta">${metaHtml}</div>
    <hr class="print-header-rule" />
  </header>
  <table><thead>${thead}</thead><tbody>${rows || emptyRow}</tbody></table>
</body></html>`;

      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "style",
        "position:fixed;width:0;height:0;border:0;visibility:hidden;"
      );
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => iframe.remove(), 400);
        }
      }, 200);
    },

    isAccepted(det) {
      const acceptVia = det.acceptedBy;
      if (acceptVia) {
        return this.isHeadOffice
          ? acceptVia == "admin-portal"
          : acceptVia == "app-login";
      }
      return false;
    },
    openVerify(item) {
      this.verifyStd = item;
      this.verifyModalVisible = true;

      this.verifyObj = {
        sID: item.id,
        userID: this.$store.state.userData.userID,
        isConditional: this.isHeadOffice,
        comment: "",
      };
    },
    async acceptVerification() {
      if (!this.verifyStd) return;
      if (this.verifyObj.isConditional && !this.verifyObj.comment) {
        this.$bvToast.toast(this.$t("Please enter the comment"), {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.verifying = true;
      this.verifyObj.comment = this.verifyObj.isConditional
        ? this.verifyObj.comment
        : "";

      const status = await this.post({
        url:
          this.$store.state.domain +
          "StudentDetails/VerifyStudent?db=" +
          this.$store.state.userData.db,
        body: this.verifyObj,
        message: this.$t("Student verified successfully."),
        context: this,
        token: this.$store.state.userData.token,
      });

      this.verifying = false;

      if (status) {
        this.verifyModalVisible = false;
        this.verifyStd = null;
        this.LoadData();
      }
    },
    async openConfirm(item) {
      const sID = item.id;
      const result = await Swal.fire({
        title: this.$t("Confirm UTP?"),
        text: this.$t(
          "Are you sure you want to confirm the UTP status for this student?"
        ),
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes"),
        cancelButtonText: this.$t("No"),
      });

      if (result.isConfirmed) {
        const status = await this.post({
          url:
            this.$store.state.domain +
            "StudentDetails/ConfirmUtp?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            sID +
            "&userID=" +
            this.$store.state.userData.userID +
            "&isConditional=false",
          body: null,
          message: this.$t("UTP status confirmed successfully."),
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.LoadData();
        }
      }
    },

    async LoadData() {
      if (this.campusID) {
        this.dataLoading = true;

        const campusId = this.campusID || this.$store.state.userData.cId;

        const selectedCat = this.isPcp ? ["Offered PCP"] : this.category || [];

        const url = this.isUtp
          ? this.$store.state.domain +
            "StudentDetails/UTPScholarshipCampusSummary?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            campusId +
            "&acceptedBy=" +
            this.acceptedBy +
            "&region=" +
            (this.region.join(",") || "") + 
            "&fiscalYear=" +
            (this.selectedFiscalYear.join(",") || "") 
            : this.$store.state.domain +
            "StudentDetails/ScholarshipSummaryForCampusBranch?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            campusId +
            "&acceptedBy=" +
            this.acceptedBy +
            "&region=" +
            (this.region.join(",") || "") +
            "&category=" +
            (selectedCat.join(",") || "");

        var obj = {
          url,
          token: this.$store.state.userData.token,
        };
        let res = await this.get(obj);
        const payload = this.parseScholarshipResponse(res);
        this.stdItem = payload?.items ?? [];
        if (payload?.totalCounts) this.scholarshipTotals = payload.totalCounts;
        this.assignedStrength =
          payload?.classStrength ?? payload?.ClassStrength ?? [];
        this.acceptedStrength =
          payload?.classAcceptedStrength ??
          payload?.ClassAcceptedStrength ??
          [];
        this.notAcceptedStrength = payload?.classNotAcceptedStrength ?? [];
        this.dataLoading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";

.scholarship-stats-card {
  row-gap: 1rem;

  .stats-card {
    border-radius: 0.5rem;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    height: 100%;
  }

  .scholarship-status-card {
    display: flex;
    flex-direction: column;

    &__head {
      margin-bottom: 0.75rem;
    }

    &__metrics {
      margin-top: auto;
      padding-top: 0.25rem;
    }

    &__metric {
      position: relative;

      &:not(:last-child)::after {
        content: "";
        position: absolute;
        top: 15%;
        right: 0;
        height: 70%;
        width: 1px;
        background: rgba(255, 255, 255, 0.28);
      }
    }

    &__value {
      font-size: 1.55rem;
      font-weight: 700;
      line-height: 1.15;
      color: #fff;
      margin-bottom: 0.15rem;
    }

    &__label {
      display: block;
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: rgba(255, 255, 255, 0.88);
      margin-bottom: 0;
    }
  }
}

/* --- Scholarship Stats Card Redesign --- */
.scholarship-card-new {
  background-color: #ffffff;
  border: 1.5px solid #f0f0f5;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  // box-shadow: 0 4px 12px 0 rgba(34, 41, 47, 0.1);
  box-shadow: 0px 5px 4px 1px rgba(34, 41, 47, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100% - 1rem);
}

.scholarship-card-new:hover {
  transform: translateY(-5px);
  // box-shadow: 0 8px 30px 0 rgba(34, 41, 47, 0.2);
}

.scholarship-card-new.border-top-primary {
  border-top: 4px solid #7367f0 !important;
}

.scholarship-card-new.border-top-success {
  border-top: 4px solid #28c76f !important;
}

.scholarship-card-new.border-top-warning {
  border-top: 4px solid #ff9f43 !important;
}

.scholarship-card-new.border-top-info {
  border-top: 4px solid #00cfe8 !important;
}

.scholarship-card-icon-container {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.scholarship-card-icon-container:hover {
  transform: scale(1.1);
}

.scholarship-card-icon-container.bg-light-primary {
  background-color: #f3f2fe;
  color: #7367f0;
}

.scholarship-card-icon-container.bg-light-success {
  background-color: #e8f9f0;
  color: #28c76f;
}

.scholarship-card-icon-container.bg-light-warning {
  background-color: #fff3e8;
  color: #ff9f43;
}

.scholarship-card-icon-container.bg-light-info {
  background-color: #e0f9fc;
  color: #00cfe8;
}

.scholarship-card-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #82868b;
  text-transform: uppercase;
}

.scholarship-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c2738;
  margin-top: 0.15rem;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.scholarship-card-divider {
  border: 0;
  border-top: 1.5px solid #f0f0f5;
  margin: 0.5rem 0;
}
</style>
